import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { observable, action, makeAutoObservable } from 'mobx';
import { DateTime } from 'src_common/utils/time/time';
import Icon from 'src/components/Icon/Icon';
import {
    AssetFlowsItemType,
    TransactionHistoryTabState,
} from 'src/components/CustomersBar/CustomerTab/TransactionHistoryTab/TransactionHistoryTab.state';
import { Pagination } from 'src/components/Pagination/PaginationNew';
import Loading from 'src/components/Loading';
import { FiltersType } from 'src/components/CustomersBar/CustomerTab/TransactionHistoryTab/TransactionHistoryTab';
import { TransactionRow } from './TransactionRow';
import {
    TableTitleCell,
    TableData,
    TableHeadRow,
    TableDataRow,
    SelectOptionsWrapper,
    SelectOptions,
} from './TransactionList.styles';
import { formatAmountWithCurrency } from 'src_common/common/amount/website-money/currency';
import { getCurrency } from 'src/AppState/sdk/staff';
import { useAppStateContext } from 'src/AppState/AppState';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { FrownIcon } from 'lucide-react';
import classNames from 'classnames';

const EmptyTransactionsState = (): JSX.Element => (
    <div className='flex flex-col items-center justify-center gap-3 py-6 text-center'>
        <FrownIcon className='text-foreground/60' />
        <div className='space-y-1'>
            <p className='font-medium text-foreground'>No transactions found</p>
            <p className='text-sm text-foreground/60'>This user has no transactions to display yet.</p>
        </div>
    </div>
);

const TimeoutTransactionsState = (): JSX.Element => (
    <div className='flex flex-col items-center justify-center gap-3 py-6 text-center'>
        <FrownIcon className='text-foreground/60' />
        <div className='space-y-1'>
            <p className='font-medium text-foreground'>Transactions request timeout</p>
            <p className='text-sm text-foreground/60'>Please try to reload the page.</p>
        </div>
    </div>
);

const METHOD_LABELS = {
    blockchain: 'Blockchain',
    'credit-card': 'Debit card',
    'bank-transfer': 'Bank transfer',
    cash: 'Cash',
    cheque: 'Cheque',
    manual: 'Manual',
    'casino-bonus': 'Casino bonus',
    'open-banking': 'Open banking',
};

export interface SpecialColumnType {
    id: 'type' | 'status';
    name: 'Type' | 'Status';
}

const COLUMNS: Array<SpecialColumnType> = [
    { id: 'type', name: 'Type' },
    { id: 'status', name: 'Status' },
];

class TransactionsListState {
    @observable public openedTransaction: number | null = null;
    @observable public selectedTransactions: { id: number; amount: number }[] = [];

    public constructor(public readonly transactionHistoryTabState: TransactionHistoryTabState) {
        makeAutoObservable(this);
    }

    @action public toggleTransaction = (assetFlow: AssetFlowsItemType): void => {
        if (this.openedTransaction === assetFlow.id) {
            this.openedTransaction = null;
        } else {
            this.openedTransaction = assetFlow.id;
        }
    };

    @action public changeStatusFilter = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        if (value !== this.transactionHistoryTabState.transactionStatusActiveFilter) {
            this.transactionHistoryTabState.onSetInitialPageChange();
            this.transactionHistoryTabState.transactionStatusActiveFilter = value;
        }
    };

    @action public changeTypeFilter = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const value = event.target.value;
        if (value !== this.transactionHistoryTabState.transactionTypeActiveFilter) {
            this.transactionHistoryTabState.onSetInitialPageChange();
            this.transactionHistoryTabState.transactionTypeActiveFilter = value;
        }
    };

    @action public handleSelectedTransaction = (assetFlow: AssetFlowsItemType): void => {
        const index = this.selectedTransactions.findIndex((el) => el.id === assetFlow.id);

        const exists = index > -1;

        if (exists) {
            this.selectedTransactions.splice(index, 1);
            return;
        }

        this.selectedTransactions.push({
            id: assetFlow.id,
            amount: assetFlow.balanceDelta,
        });
    };

    @action public handleSelectAll = (assetFlows: AssetFlowsItemType[], hasAllRowsSelected: boolean): void => {
        if (hasAllRowsSelected) {
            this.selectedTransactions = [];
            return;
        }

        this.selectedTransactions = assetFlows.map((el) => ({ id: el.id, amount: el.balanceDelta }));
    };
}

interface TransactionsListPropsType {
    name: string;
    state: TransactionHistoryTabState;
    userId: number;
    availableFilters: FiltersType;
}

export const TransactionsList = observer(
    'TransactionsList',
    ({ state, availableFilters }: TransactionsListPropsType) => {
        const appState = useAppStateContext();
        const { assetFlowsList } = state;
        const [localState] = useState(() => new TransactionsListState(state));
        const totalTransactionAmount = localState.selectedTransactions.reduce((total, transaction) => {
            return total + transaction.amount;
        }, 0);

        const elements = assetFlowsList.map((assetFlow, idx) => {
            const { transaction, type, date, balanceDelta, balanceAfter, currency } = assetFlow;
            const balanceAfterNew =
                balanceAfter === null || balanceAfter === undefined
                    ? undefined
                    : appState.configState.precision.newFromAnything(balanceAfter);

            const { details, tags } = transaction;
            const adjustmentType =
                tags.adjustmentType !== undefined && tags.adjustmentType !== null ? tags.adjustmentType : null;
            const methodType = details.paymentMethod?.type;
            const gameName = details.gameName;
            const methodLabel = methodType === undefined ? '' : METHOD_LABELS[methodType];
            const method = adjustmentType ?? methodLabel;
            const cashout = details.cashOut;
            const isFreeBet =
                tags.freebetCredits !== undefined && tags.freebetCredits !== null
                    ? tags.freebetCredits.length > 0
                    : false;
            const amountNegative = balanceDelta < 0;

            const isSelected = localState.selectedTransactions.findIndex((el) => el.id === assetFlow.id) > -1;

            const el = (
                <TableDataRow
                    className={classNames('transaction-list__row', {
                        '!bg-secondary': isSelected,
                    })}
                    key={idx}
                    onClick={(): void => localState.toggleTransaction(assetFlow)}
                >
                    <TableData data-test='transaction-checkbox'>
                        <Checkbox
                            isChecked={isSelected}
                            isDisabled={false}
                            onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                localState.handleSelectedTransaction(assetFlow);
                                return;
                            }}
                        >
                            <div />
                        </Checkbox>
                    </TableData>

                    <TableData data-test='transaction-date'>
                        {DateTime.from(date)?.format('YYYY-MM-DD') ?? ''}
                    </TableData>
                    <TableData data-test='transaction-id'>{assetFlow.id}</TableData>
                    <TableData data-test='bet-id'>{transaction.id}</TableData>
                    <TableData data-test='transaction-method'>{method}</TableData>
                    <TableData data-test='game'>{gameName}</TableData>
                    <TableData data-test='transaction-type'>
                        {cashout === true && type !== 'bet-placement' ? 'cash-out' : type}
                    </TableData>
                    <TableData data-test='transaction-status'>{transaction.status}</TableData>
                    <TableData
                        amountNegative={amountNegative}
                        data-test='transaction-amount'
                    >
                        {type === 'bet-placement' && isFreeBet ? (
                            <span>
                                <Icon name='gift' /> Free
                            </span>
                        ) : (
                            formatAmountWithCurrency(
                                getCurrency(currency),
                                appState.configState.precision.newFromAnything(balanceDelta)
                            )
                        )}
                    </TableData>
                    <TableData data-test='transaction-balance'>
                        {formatAmountWithCurrency(getCurrency(currency), balanceAfterNew)}
                    </TableData>
                </TableDataRow>
            );

            if (localState.openedTransaction === assetFlow.id) {
                return [
                    el,
                    <TransactionRow
                        key={assetFlow.id}
                        assetFlow={assetFlow}
                    />,
                ];
            }

            return [el];
        });

        const specialColumns = COLUMNS.map((column) => {
            const onChange = column.id === 'status' ? localState.changeStatusFilter : localState.changeTypeFilter;
            const value =
                column.id === 'status' ? state.transactionStatusActiveFilter : state.transactionTypeActiveFilter;
            state.handlerStatusType(column.id, value);

            let options;
            if (availableFilters[column.id].length > 0) {
                options = (
                    <SelectOptionsWrapper>
                        {column.name}
                        <SelectOptions
                            value={value}
                            onChange={onChange}
                        >
                            <option value='all'>All</option>
                            {availableFilters[column.id].map((value) => (
                                <option
                                    key={value.id}
                                    value={value.id}
                                >
                                    {value.name}
                                </option>
                            ))}
                        </SelectOptions>
                    </SelectOptionsWrapper>
                );
            }

            return <th key={column.id}>{options}</th>;
        });

        const allRowsSelected = (): boolean => {
            if (assetFlowsList.length === 0) {
                return false;
            }

            return assetFlowsList.every((assetFlow) =>
                localState.selectedTransactions.some((selected) => selected.id === assetFlow.id)
            );
        };

        const hasAllRowsSelected = allRowsSelected();

        return (
            <div>
                <table>
                    <thead>
                        <TableHeadRow>
                            <TableTitleCell
                                onClick={() => localState.handleSelectAll(assetFlowsList, hasAllRowsSelected)}
                                className='cursor-pointer'
                            >
                                <Checkbox
                                    isChecked={hasAllRowsSelected}
                                    isDisabled={false}
                                    onChange={() => {
                                        return;
                                    }}
                                >
                                    <div />
                                </Checkbox>
                            </TableTitleCell>
                            <TableTitleCell>Date</TableTitleCell>
                            <TableTitleCell>Transaction ID</TableTitleCell>
                            <TableTitleCell>Bet ID</TableTitleCell>
                            <TableTitleCell>Method</TableTitleCell>
                            <TableTitleCell>Game</TableTitleCell>
                            {specialColumns}
                            <TableTitleCell>Amount</TableTitleCell>
                            <TableTitleCell>Balance</TableTitleCell>
                        </TableHeadRow>
                    </thead>
                    <tbody>{elements}</tbody>
                </table>

                {state.shouldDisplayEmptyMessage && <EmptyTransactionsState />}

                {state.shouldDisplayTimeoutMessage && <TimeoutTransactionsState />}

                {localState.selectedTransactions.length > 0 ? (
                    <div className='grid grid-cols-2 border-t border-b border-spacing-1'>
                        <p>Total Selected</p>
                        <p>{localState.selectedTransactions.length}</p>

                        <p>Total amount</p>
                        <p>{(totalTransactionAmount / 100).toFixed(2)}</p>
                    </div>
                ) : null}

                <Pagination
                    className='transaction-list__pagination'
                    perPage={state.perPage}
                    page={state.page}
                    total={state.assetFlowsCurrentCount}
                    currentVisibleItems={state.assetFlowsListLength}
                    onPageChange={state.onPageChange}
                />
                <Loading
                    active={state.isAssetFlowsListLoading}
                    message='Loading transactions'
                />
            </div>
        );
    }
);
